import React, { useEffect, useRef, useState } from "react";
import { FiPlus, FiX, FiInfo } from "react-icons/fi";
import styles from "./WhiteLabelDomains.module.css";

// Updated regex to include localhost and IP addresses
const DOMAIN_RE = /^(?:(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}|localhost|\d{1,3}(?:\.\d{1,3}){3})$/i;
const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;



export default function WhiteLabelDomains({ app_name, region, initialDomains = [] }) {
  const token = localStorage.getItem("token") || "";
  const [domains, setDomains] = useState(() => Array.isArray(initialDomains) ? initialDomains : []);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [showExamples, setShowExamples] = useState(false);
  const inputRef = useRef(null);

  const normalize = (s) => (s || "").trim().toLowerCase();

  const isValidDomain = (domain) => {
    // Check against regex
    if (!DOMAIN_RE.test(domain)) {
      return false;
    }

    // Additional validation for IP addresses
    if (/^\d{1,3}(?:\.\d{1,3}){3}$/.test(domain)) {
      const parts = domain.split('.').map(Number);
      return parts.every(part => part >= 0 && part <= 255);
    }

    return true;
  };

  const addDomain = (raw) => {
    const rawVal = raw !== undefined && raw !== null ? raw : input;
    const candidate = normalize(rawVal);

    if (!candidate) {
      setMsg("Type a domain first");
      return;
    }

    // Remove any trailing comma or whitespace
    const cleanCandidate = candidate.replace(/,\s*$/, '');

    if (!cleanCandidate) {
      setMsg("Type a domain first");
      return;
    }

    // Validate domain
    if (!isValidDomain(cleanCandidate)) {
      setMsg("Invalid domain format. Use: example.com, localhost, or 127.0.0.1");
      return;
    }

    // case-insensitive dedupe
    const found = domains.some((d) => normalize(d) === cleanCandidate);
    if (found) {
      setMsg("Domain already added");
      setInput("");
      inputRef.current?.focus();
      return;
    }

    setDomains((prev) => [...prev, cleanCandidate]);
    setInput("");
    setMsg("");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const removeDomain = (idx) => {
    setDomains((prev) => prev.filter((_, i) => i !== idx));
    setMsg("");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleSubmit = async (e) => {
    e && e.preventDefault();
    if (domains.length === 0) {
      setMsg("Add at least one domain before saving");
      return;
    }
    setLoading(true);
    setMsg("");
    try {
      const region_prefix = region.toLowerCase()
      const res = await fetch(
        `https://${region_prefix}.apps2.${backend_api_base_url}/update_whitelabel_domains?app_name=${encodeURIComponent(
          app_name
        )}`,
        {
          method: "PUT",
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ whitelabel_domains: domains }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || data.error || "Failed to update");
      }
      setMsg("Domains updated ✓");
    } catch (err) {
      setMsg(`Error: ${err.message}`);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  // Enter or comma adds domain
  const handleKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addDomain();
    } else if (e.key === ",") {
      e.preventDefault();
      addDomain(input + ',');
    } else if (e.key === "Escape") {
      setInput("");
    }
  };

  const handleAddClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addDomain();
  };

  const handleReset = () => {
    setDomains(Array.isArray(initialDomains) ? initialDomains : []);
    setInput("");
    setMsg("");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleExampleClick = (example) => {
    setInput(example);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h2 className={styles.title}>Whitelabel Domains</h2>
          <button
            type="button"
            className={styles.infoBtn}
            onClick={() => setShowExamples(!showExamples)}
            aria-label="Show examples"
            title="Show examples"
          >
            <FiInfo size={16} />
          </button>
        </div>
        <small className={styles.subtitle}>
          Add domains that will be recognized as whitelabel hosts
        </small>

        {showExamples && (
          <div className={styles.examplesPanel}>
            <div className={styles.examplesHeader}>
              <span className={styles.examplesTitle}>Domain Examples:</span>
              <button
                type="button"
                className={styles.examplesClose}
                onClick={() => setShowExamples(false)}
                aria-label="Close examples"
              >
                <FiX size={16} />
              </button>
            </div>
            <div className={styles.examplesGrid}>
              <div className={styles.exampleCategory}>
                <span className={styles.categoryTitle}>Production</span>
                <button
                  type="button"
                  className={styles.exampleBtn}
                  onClick={() => handleExampleClick("example.com")}
                >
                  example.com
                </button>
                <button
                  type="button"
                  className={styles.exampleBtn}
                  onClick={() => handleExampleClick("app.example.com")}
                >
                  app.example.com
                </button>
                <button
                  type="button"
                  className={styles.exampleBtn}
                  onClick={() => handleExampleClick("myapp.com")}
                >
                  myapp.com
                </button>
              </div>
              <div className={styles.exampleCategory}>
                <span className={styles.categoryTitle}>Local Development</span>
                <button
                  type="button"
                  className={styles.exampleBtn}
                  onClick={() => handleExampleClick("localhost")}
                >
                  localhost
                </button>
                <button
                  type="button"
                  className={styles.exampleBtn}
                  onClick={() => handleExampleClick("127.0.0.1")}
                >
                  127.0.0.1
                </button>
                <button
                  type="button"
                  className={styles.exampleBtn}
                  onClick={() => handleExampleClick("192.168.1.100")}
                >
                  192.168.1.100
                </button>
              </div>
              <div className={styles.exampleCategory}>
                <span className={styles.categoryTitle}>Staging/Test</span>
                <button
                  type="button"
                  className={styles.exampleBtn}
                  onClick={() => handleExampleClick("staging.example.com")}
                >
                  staging.example.com
                </button>
                <button
                  type="button"
                  className={styles.exampleBtn}
                  onClick={() => handleExampleClick("test.local")}
                >
                  test.local
                </button>
                <button
                  type="button"
                  className={styles.exampleBtn}
                  onClick={() => handleExampleClick("dev.myapp.com")}
                >
                  dev.myapp.com
                </button>
              </div>
            </div>
            <div className={styles.examplesNote}>
              <FiInfo size={12} />
              <span>Localhost and IP addresses are allowed for development</span>
            </div>
          </div>
        )}
      </div>

      <div className={styles.form}>
        <div className={styles.inputRow}>
          <input
            ref={inputRef}
            className={styles.input}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Enter domain (example.com, localhost, 127.0.0.1)"
            aria-label="Add domain"
            disabled={loading}
          />
          <button
            type="button"
            className={styles.addBtn}
            onClick={handleAddClick}
            aria-label="Add domain"
            title="Add domain"
            disabled={loading}
          >
            <FiPlus size={16} />
          </button>
        </div>

        <div className={styles.hint}>
          <FiInfo size={12} />
          <span>Press Enter or comma to add. Supports domains, localhost, and IP addresses.</span>
        </div>

        <div className={styles.chips}>
          {domains.length === 0 ? (
            <div className={styles.empty}>No domains yet. Add your first domain above.</div>
          ) : (
            domains.map((d, i) => {
              const isLocal = d === 'localhost' || /^\d{1,3}(?:\.\d{1,3}){3}$/.test(d);
              return (
                <div
                  key={`${d}-${i}`}
                  className={`${styles.chip} ${isLocal ? styles.localChip : ''}`}
                >
                  <span className={styles.chipText}>{d}</span>
                  {isLocal && (
                    <span className={styles.localBadge} title="Local development domain">
                      Local
                    </span>
                  )}
                  <button
                    type="button"
                    className={styles.chipRemove}
                    onClick={() => removeDomain(i)}
                    aria-label={`Remove ${d}`}
                    title={`Remove ${d}`}
                  >
                    <FiX size={14} />
                  </button>
                </div>
              );
            })
          )}
        </div>

        <div className={styles.controls}>
          <button
            type="button"
            className={styles.saveBtn}
            onClick={handleSubmit}
            disabled={loading || domains.length === 0}
          >
            {loading ? "Saving..." : "Save domains"}
          </button>
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={handleReset}
            disabled={loading}
          >
            Reset
          </button>
        </div>

        {msg && <div className={styles.message}>{msg}</div>}
      </div>
    </div>
  );
}